import { Button } from "../ui/Button";
import { t } from "../../i18n/strings";
import type { Locale } from "../../templates/types";

/**
 * §16 / §21 non-negotiable #12: a mic that's present but non-functional
 * implies voice works when it doesn't — exactly the dishonesty this product
 * forbids. So there is no disabled/greyed variant for "unsupported": when
 * `canListen` is false this renders nothing at all, and the composer shows
 * a one-line explanation instead (see Composer.tsx / "voice.unsupported").
 * `disabled` here is a DIFFERENT thing — it tracks the composer's own
 * enabled state (e.g. paused/awaiting model), same as the Send button.
 */
export function MicButton({
  canListen,
  listening,
  onToggle,
  locale = "en",
  disabled = false,
}: {
  canListen: boolean;
  listening: boolean;
  onToggle: () => void;
  locale?: Locale;
  disabled?: boolean;
}) {
  if (!canListen) return null;

  const label = listening ? t(locale, "voice.mic.stop") : t(locale, "voice.mic.start");

  return (
    <Button
      type="button"
      variant={listening ? "danger" : "secondary"}
      aria-pressed={listening}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onToggle}
      // Visibly different while listening, per the existing Button/Pill
      // visual vocabulary (variant swap) plus a pulse to read as "live."
      className={listening ? "animate-pulse" : undefined}
    >
      <span aria-hidden="true">{listening ? "●" : "🎙"}</span>
    </Button>
  );
}
